import React, { useState, useEffect } from 'react';
import { DashboardLayout } from './Dashboard';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Users, Search, MoreVertical, Shield, Trash2, Edit, 
  Crown, UserCheck, User, Briefcase, KeyRound, Send, Copy, Loader2, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const UsersPage = () => {
  const { user: currentUser, token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editRole, setEditRole] = useState('');
  // Password reset state
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetUser, setResetUser] = useState(null);
  const [resetMode, setResetMode] = useState(null); // 'generate' | 'send_link'
  const [resetResult, setResetResult] = useState(null);
  const [resetSubmitting, setResetSubmitting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [token]);

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditRole = async () => {
    if (!selectedUser || !editRole) return;

    try {
      const response = await fetch(`${API_URL}/api/users/${selectedUser.user_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ role: editRole })
      });

      if (response.ok) {
        toast.success('User role updated!');
        setEditDialogOpen(false);
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update role');
      }
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleDeleteUser = async (userToDelete) => {
    if (!confirm(`Are you sure you want to delete ${userToDelete.name}? This action cannot be undone.`)) return;

    try {
      const response = await fetch(`${API_URL}/api/users/${userToDelete.user_id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (response.ok) {
        toast.success('User deleted!');
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setEditRole(user.role);
    setEditDialogOpen(true);
  };

  const openResetDialog = (user) => {
    setResetUser(user);
    setResetMode(null);
    setResetResult(null);
    setResetDialogOpen(true);
  };

  const handleResetPassword = async (mode) => {
    if (!resetUser) return;
    setResetMode(mode);
    setResetSubmitting(true);
    setResetResult(null);
    try {
      const response = await fetch(`${API_URL}/api/admin/users/${resetUser.user_id}/reset-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ mode })
      });
      if (response.ok) {
        const data = await response.json();
        setResetResult(data);
        toast.success(mode === 'generate' ? 'Temporary password generated!' : 'Reset link sent!');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to reset password');
      }
    } catch { toast.error('Failed to reset password'); }
    finally { setResetSubmitting(false); }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'super_admin': return <Crown className="w-4 h-4" />;
      case 'admin': return <Shield className="w-4 h-4" />;
      case 'manager': return <Briefcase className="w-4 h-4" />;
      default: return <User className="w-4 h-4" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin': return 'text-yellow-400 bg-yellow-400/10';
      case 'admin': return 'text-primary bg-primary/10';
      case 'manager': return 'text-blue-400 bg-blue-400/10';
      default: return 'text-white/60 bg-white/10';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  };

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <DashboardLayout title="User Management">
      <div className="space-y-6">
        {/* Search */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <Input
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-white/30"
              data-testid="search-users-input"
            />
          </div>
          <div className="flex items-center gap-2 text-xs text-white/60">
            <Users className="w-4 h-4" />
            <span>{users.length} total users</span>
          </div>
        </div>

        {/* Role Legend */}
        <div className="flex flex-wrap gap-4 p-4 bg-black/40 border border-white/10 rounded-sm">
          <div className="flex items-center gap-2">
            <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-sm ${getRoleColor('super_admin')}`}>
              {getRoleIcon('super_admin')} Super Admin
            </span>
            <span className="text-white/40 text-xs">Full system access</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-sm ${getRoleColor('admin')}`}>
              {getRoleIcon('admin')} Admin
            </span>
            <span className="text-white/40 text-xs">User & document management</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-sm ${getRoleColor('manager')}`}>
              {getRoleIcon('manager')} Manager
            </span>
            <span className="text-white/40 text-xs">Team document sharing</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-sm ${getRoleColor('user')}`}>
              {getRoleIcon('user')} User
            </span>
            <span className="text-white/40 text-xs">Basic access</span>
          </div>
        </div>

        {/* Users Table */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="animate-pulse bg-black/40 border border-white/10 rounded-sm p-4 h-20" />
            ))}
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <h3 className="text-xl text-white mb-2">No Users Found</h3>
            <p className="text-white/60">
              {searchQuery ? 'Try adjusting your search' : 'No users in the system'}
            </p>
          </div>
        ) : (
          <div className="bg-black/40 border border-white/10 rounded-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-widest text-white/40">User</th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-widest text-white/40">Email</th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-widest text-white/40">Role</th>
                    <th className="text-left p-4 text-xs font-mono uppercase tracking-widest text-white/40">Joined</th>
                    <th className="text-right p-4 text-xs font-mono uppercase tracking-widest text-white/40">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr 
                      key={user.user_id} 
                      className="border-b border-white/5 hover:bg-white/5 transition-colors"
                      data-testid={`user-row-${user.user_id}`}
                    >
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {user.picture ? (
                            <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-semibold">
                              {user.name?.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <p className="text-white font-medium">{user.name}</p>
                            {user.user_id === currentUser?.user_id && (
                              <span className="text-xs text-primary">(You)</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4 text-white/60">{user.email}</td>
                      <td className="p-4">
                        <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-sm w-fit ${getRoleColor(user.role)}`}>
                          {getRoleIcon(user.role)}
                          {user.role?.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="p-4 text-white/40 text-sm">{formatDate(user.created_at)}</td>
                      <td className="p-4 text-right">
                        {user.user_id !== currentUser?.user_id && currentUser?.role === 'super_admin' && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`user-menu-${user.user_id}`}>
                                <MoreVertical className="w-4 h-4 text-white/60" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-[#0A0A0A] border-white/10">
                              <DropdownMenuItem 
                                onClick={() => openEditDialog(user)}
                                className="text-white hover:bg-white/5 cursor-pointer"
                              >
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Role
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => openResetDialog(user)}
                                className="text-white hover:bg-white/5 cursor-pointer"
                                data-testid={`reset-pwd-${user.user_id}`}
                              >
                                <KeyRound className="w-4 h-4 mr-2" />
                                Reset Password
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleDeleteUser(user)}
                                className="text-red-400 hover:bg-red-400/10 cursor-pointer"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete User
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Edit Role Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="bg-[#0A0A0A] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Edit User Role
              </DialogTitle>
              <DialogDescription className="text-white/60">
                Change role for {selectedUser?.name}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <Select value={editRole} onValueChange={setEditRole}>
                <SelectTrigger className="bg-black/50 border-white/10 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  {currentUser?.role === 'super_admin' && (
                    <SelectItem value="super_admin" className="text-white">
                      <div className="flex items-center gap-2">
                        <Crown className="w-4 h-4 text-yellow-400" />
                        Super Admin
                      </div>
                    </SelectItem>
                  )}
                  <SelectItem value="admin" className="text-white">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-primary" />
                      Admin
                    </div>
                  </SelectItem>
                  <SelectItem value="manager" className="text-white">
                    <div className="flex items-center gap-2">
                      <Briefcase className="w-4 h-4 text-blue-400" />
                      Manager
                    </div>
                  </SelectItem>
                  <SelectItem value="user" className="text-white">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-white/60" />
                      User
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>

              <div className="flex gap-3">
                <Button 
                  variant="outline"
                  onClick={() => setEditDialogOpen(false)}
                  className="flex-1 border-white/10 text-white hover:bg-white/5"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleEditRole}
                  className="flex-1 bg-primary text-black hover:bg-primary/90"
                  data-testid="save-role-btn"
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={resetDialogOpen} onOpenChange={(v) => { if (!v) { setResetDialogOpen(false); setResetResult(null); setResetMode(null); } }}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                <KeyRound className="w-5 h-5 text-primary" />
                Reset Password
              </DialogTitle>
              <DialogDescription className="text-white/60">
                Reset password for {resetUser?.name} ({resetUser?.email})
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              {!resetResult && !resetMode && (
                <div className="space-y-3">
                  <p className="text-white/60 text-sm">Choose a reset method:</p>
                  
                  <button
                    onClick={() => handleResetPassword('generate')}
                    disabled={resetSubmitting}
                    className="w-full p-4 border border-white/10 rounded-sm hover:bg-white/5 transition-colors text-left group"
                    data-testid="reset-generate-btn"
                  >
                    <div className="flex items-start gap-3">
                      <KeyRound className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-white font-medium group-hover:text-primary transition-colors">Generate Temporary Password</p>
                        <p className="text-white/40 text-xs mt-1">Create a one-time password. The user will be required to change it on their next login.</p>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => handleResetPassword('send_link')}
                    disabled={resetSubmitting}
                    className="w-full p-4 border border-white/10 rounded-sm hover:bg-white/5 transition-colors text-left group"
                    data-testid="reset-send-link-btn"
                  >
                    <div className="flex items-start gap-3">
                      <Send className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-white font-medium group-hover:text-blue-400 transition-colors">Send Reset Link via Email</p>
                        <p className="text-white/40 text-xs mt-1">Send a secure password reset link to the user's email. The link expires in 24 hours.</p>
                      </div>
                    </div>
                  </button>
                </div>
              )}

              {resetSubmitting && (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-8 h-8 text-primary animate-spin" />
                </div>
              )}

              {resetResult && resetMode === 'generate' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-green-400">
                    <CheckCircle className="w-5 h-5" />
                    <p className="font-medium">Temporary Password Generated</p>
                  </div>
                  <div className="p-4 bg-white/5 border border-white/10 rounded-sm">
                    <p className="text-white/40 text-xs mb-2">Temporary password for {resetResult.user_email}:</p>
                    <div className="flex items-center gap-3">
                      <code className="text-primary text-lg font-mono flex-1 select-all" data-testid="temp-password-display">
                        {resetResult.temp_password}
                      </code>
                      <Button
                        variant="outline" size="sm"
                        onClick={() => copyToClipboard(resetResult.temp_password)}
                        className="border-white/10 text-white hover:bg-white/5"
                        data-testid="copy-temp-pwd-btn"
                      >
                        <Copy className="w-3 h-3 mr-1" /> Copy
                      </Button>
                    </div>
                  </div>
                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-sm">
                    <p className="text-yellow-400 text-xs">
                      The user will be required to set a new password on their next login. Share this password securely.
                    </p>
                  </div>
                </div>
              )}

              {resetResult && resetMode === 'send_link' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-green-400">
                    <CheckCircle className="w-5 h-5" />
                    <p className="font-medium">Reset Link Generated</p>
                  </div>
                  <div className="p-4 bg-white/5 border border-white/10 rounded-sm space-y-3">
                    <div>
                      <p className="text-white/40 text-xs mb-1">Email Status:</p>
                      <p className={`text-sm font-medium ${resetResult.email_status === 'sent' ? 'text-green-400' : resetResult.email_status === 'skipped' ? 'text-yellow-400' : 'text-red-400'}`}>
                        {resetResult.email_status === 'sent' ? 'Email sent successfully' : resetResult.email_status === 'skipped' ? 'Email service not configured — share link manually' : 'Email failed to send'}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/40 text-xs mb-1">Reset Link (expires in {resetResult.expires_in}):</p>
                      <div className="flex items-center gap-2">
                        <code className="text-primary text-xs font-mono flex-1 break-all select-all bg-black/30 p-2 rounded" data-testid="reset-link-display">
                          {resetResult.reset_url}
                        </code>
                        <Button
                          variant="outline" size="sm"
                          onClick={() => copyToClipboard(resetResult.reset_url)}
                          className="border-white/10 text-white hover:bg-white/5 flex-shrink-0"
                          data-testid="copy-reset-link-btn"
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                  {resetResult.email_status !== 'sent' && (
                    <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-sm">
                      <p className="text-blue-400 text-xs">
                        To enable automatic email delivery, add your Resend API key (RESEND_API_KEY) to the backend environment variables.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {resetResult && (
                <Button
                  onClick={() => { setResetDialogOpen(false); setResetResult(null); setResetMode(null); }}
                  className="w-full bg-primary text-black hover:bg-primary/90"
                  data-testid="reset-done-btn"
                >
                  Done
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default UsersPage;
